import {
  Container,
  Heading,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  Text,
  useToast,
  Box,
  useColorModeValue,
  HStack,
  Icon,
  Grid,
  Divider,
  useToken,
  Badge,
  Flex,
  Card,
  CardBody,
  SimpleGrid,
} from '@chakra-ui/react';
import { EnvelopeSimple, Phone, MapPin, Compass, Clock, Users } from '@phosphor-icons/react';
export default function Contact() {
  const toast = useToast();

  // Get resolved theme tokens for consistent styling
  const [borderLight, borderDefault, borderHeavy, textMuted, textDefault, interactivePrimary] = useToken(
    'colors',
    ['border.light', 'border.default', 'border.heavy', 'text.muted', 'text.default', 'interactive.primary']
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement actual contact form submission logic (e.g., send to backend/email service)
    console.log("Form submitted (placeholder)");
    toast({
      title: "Message Sent",
      description: "Thank you for your message. We'll get back to you within 24 hours.",
      status: "success",
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    });
    // Optionally clear the form: e.target.reset();
  };

  return (
    <Box 
      bg="bg.default" 
      minH="100vh" 
      position="relative" 
      _before={{
        content: '""',
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        opacity: useColorModeValue(0.03, 0.05),
        pointerEvents: 'none',
        bgImage: useColorModeValue('var(--texture-parchment)', 'var(--texture-leather)'),
        bgRepeat: 'repeat',
        bgSize: '600px',
        bgBlendMode: useColorModeValue('soft-light', 'multiply'),
        zIndex: 0,
      }}
    >
      {/* Enhanced Journal Binding / Spine */}
      <Box
        position="fixed"
        left={0}
        top={0}
        bottom={0}
        width={{ base: "6px", md: "18px" }}
        bgGradient="linear(to-r, border.heavy, border.default, border.light)"
        boxShadow="3px 0 8px rgba(0,0,0,0.2)"
        zIndex={15}
        display={{ base: 'none', md: 'block' }}
        _before={{
          content: '""',
          position: 'absolute',
          left: '5px',
          top: '5%',
          bottom: '5%',
          width: '4px',
          bgColor: 'rgba(255,255,255,0.15)',
          borderRadius: 'full',
          boxShadow: 'inset 1px 0 2px rgba(0,0,0,0.2)'
        }}
        _after={{
          content: '""',
          position: 'absolute',
          left: '12px',
          top: '8%',
          bottom: '8%',
          width: '2px',
          bgColor: 'rgba(255,255,255,0.2)',
          opacity: 0.8,
          borderRadius: 'full'
        }}
      />

      {/* Enhanced Page Marker */}
      <Box
        position="fixed"
        bottom="30px"
        left={{ base: "15px", md: "35px" }}
        fontFamily="handwritten"
        fontSize={{ base: "sm", md: "md" }}
        color="text.muted"
        fontWeight="medium"
        zIndex={10}
        p={{ base: "4px 10px", md: "6px 12px" }}
        bg={useColorModeValue('rgba(240, 230, 210, 0.8)', 'rgba(44, 35, 27, 0.7)')}
        borderRadius="full"
        boxShadow="md"
        border="2px solid"
        borderColor="border.light"
        transform="rotate(-2deg)"
        _hover={{ transform: "rotate(0deg)", transition: "all 0.3s ease" }}
      >
        <Flex align="center" gap={2}>
          <Icon as={EnvelopeSimple} boxSize={4} />
          <Text>Contact</Text>
        </Flex>
      </Box>

      {/* Main Content Area */}
      <Box pl={{ base: 0, md: "20px" }} position="relative" zIndex={1}>
        <Container
          maxW="container.xl"
          py={{ base: 12, md: 20 }}
          px={{ base: 4, md: 8 }}
        >
          {/* Hero Section */}
          <VStack spacing={6} textAlign="center" py={{ base: 8, md: 12 }}>
            <HStack spacing={4} align="center">
              <Icon as={MapPin} boxSize={6} color={interactivePrimary} />
              <Heading 
                as="h1" 
                size="2xl" 
                color={textDefault} 
                fontFamily="accent"
                letterSpacing="wide"
              >
                Contact Us
              </Heading>
              <Icon as={Compass} boxSize={6} color={interactivePrimary} />
            </HStack>
            
            <Text 
              fontSize={{ base: 'lg', md: 'xl' }} 
              color={textMuted} 
              maxW="3xl"
              lineHeight="tall"
            >
              Have questions about megalithic sites, research collaboration, or contributing data? 
              We'd love to hear from you and help with your inquiries.
            </Text>
          </VStack>

          {/* Simple Icon Divider */}
          <Flex align="center" justify="center" my={8}>
            <Icon as={MapPin} boxSize={5} color={interactivePrimary} opacity={0.7} />
            <Box w="60px" h="1px" bg={borderDefault} mx={3} opacity={0.5} />
            <Icon as={Compass} boxSize={6} color={interactivePrimary} opacity={0.8} />
            <Box w="60px" h="1px" bg={borderDefault} mx={3} opacity={0.5} />
            <Icon as={EnvelopeSimple} boxSize={5} color={interactivePrimary} opacity={0.7} />
          </Flex>

          {/* Contact Methods Cards */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={12}>
            <Card
              bg={useColorModeValue('bg.surface', 'bg.surface')}
              border="2px solid"
              borderColor={borderLight}
              borderRadius="lg"
              transition="all 0.3s ease"
              _hover={{
                transform: 'translateY(-4px)',
                boxShadow: 'lg',
                borderColor: borderDefault,
              }}
            >
              <CardBody textAlign="center" p={6}>
                <VStack spacing={4}>
                  <Flex
                    w="60px"
                    h="60px"
                    bg={interactivePrimary}
                    color="white"
                    borderRadius="full"
                    justify="center"
                    align="center"
                    boxShadow="md"
                  >
                    <Icon as={EnvelopeSimple} boxSize={7} weight="duotone" />
                  </Flex>
                  <VStack spacing={2}>
                    <Heading size="md" fontFamily="heading" color={textDefault}>
                      Email Us
                    </Heading>
                    <Text color={textMuted} fontSize="sm">
                      General inquiries and support
                    </Text>
                    <Text fontWeight="medium" color={interactivePrimary}>
                      admin@megalithmaps.com
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            <Card
              bg={useColorModeValue('bg.surface', 'bg.surface')}
              border="2px solid"
              borderColor={borderLight}
              borderRadius="lg"
              transition="all 0.3s ease"
              _hover={{
                transform: 'translateY(-4px)',
                boxShadow: 'lg',
                borderColor: borderDefault,
              }}
            >
              <CardBody textAlign="center" p={6}>
                <VStack spacing={4}>
                  <Flex
                    w="60px"
                    h="60px"
                    bg={interactivePrimary}
                    color="white"
                    borderRadius="full"
                    justify="center"
                    align="center"
                    boxShadow="md"
                  >
                    <Icon as={Clock} boxSize={7} weight="duotone" />
                  </Flex>
                  <VStack spacing={2}>
                    <Heading size="md" fontFamily="heading" color={textDefault}>
                      Response Time
                    </Heading>
                    <Text color={textMuted} fontSize="sm">
                      We typically respond within
                    </Text>
                    <Text fontWeight="medium" color={interactivePrimary}>
                      24 hours
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            <Card
              bg={useColorModeValue('bg.surface', 'bg.surface')}
              border="2px solid"
              borderColor={borderLight}
              borderRadius="lg"
              transition="all 0.3s ease"
              _hover={{
                transform: 'translateY(-4px)',
                boxShadow: 'lg',
                borderColor: borderDefault,
              }}
            >
              <CardBody textAlign="center" p={6}>
                <VStack spacing={4}>
                  <Flex
                    w="60px"
                    h="60px"
                    bg={interactivePrimary}
                    color="white"
                    borderRadius="full"
                    justify="center"
                    align="center"
                    boxShadow="md"
                  >
                    <Icon as={Users} boxSize={7} weight="duotone" />
                  </Flex>
                  <VStack spacing={2}>
                    <Heading size="md" fontFamily="heading" color={textDefault}>
                      Community
                    </Heading>
                    <Text color={textMuted} fontSize="sm">
                      Join our research community
                    </Text>
                    <Text fontWeight="medium" color={interactivePrimary}>
                      Discord & Forums
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Contact Form Section */}
          <Box
            bg={useColorModeValue('bg.surface', 'bg.surface')}
            p={{ base: 8, md: 12 }}
            borderRadius="xl"
            border="2px solid"
            borderColor={borderLight}
            boxShadow="lg"
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              opacity: useColorModeValue(0.08, 0.12),
              pointerEvents: 'none',
              bgImage: 'var(--texture-linen)',
              bgRepeat: 'repeat',
              bgSize: '300px',
              bgBlendMode: useColorModeValue('soft-light', 'multiply'),
              borderRadius: 'inherit',
              zIndex: 0,
            }}
          >
            <VStack spacing={8} position="relative" zIndex={1}>
              <VStack spacing={4} textAlign="center">
                <Heading 
                  size="lg" 
                  fontFamily="accent" 
                  color={textDefault}
                  letterSpacing="wide"
                >
                  Send us a Message
                </Heading>
                <Text color={textMuted} maxW="2xl">
                  Whether you have questions about specific sites, want to contribute research, 
                  or need technical support, we're here to help.
                </Text>
              </VStack>

              <Box w="full" maxW="2xl">
                <form onSubmit={handleSubmit}>
                  <VStack spacing={6}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
                      <FormControl isRequired>
                        <FormLabel fontWeight="medium" color={textMuted} fontFamily="heading">
                          Name
                        </FormLabel>
                        <Input
                          type="text"
                          name="name"
                          placeholder="Your Name"
                          focusBorderColor={interactivePrimary}
                          borderColor={borderDefault}
                          bg={useColorModeValue('whiteAlpha.900', 'blackAlpha.300')}
                          borderRadius="md"
                          size="lg"
                          _placeholder={{ color: textMuted }}
                          _hover={{ borderColor: borderDefault }}
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel fontWeight="medium" color={textMuted} fontFamily="heading">
                          Email
                        </FormLabel>
                        <Input
                          type="email"
                          name="email"
                          placeholder="your.email@example.com"
                          focusBorderColor={interactivePrimary}
                          borderColor={borderDefault}
                          bg={useColorModeValue('whiteAlpha.900', 'blackAlpha.300')}
                          borderRadius="md"
                          size="lg"
                          _placeholder={{ color: textMuted }}
                          _hover={{ borderColor: borderDefault }}
                        />
                      </FormControl>
                    </SimpleGrid>

                    <FormControl isRequired>
                      <FormLabel fontWeight="medium" color={textMuted} fontFamily="heading">
                        Subject
                      </FormLabel>
                      <Input
                        type="text"
                        name="subject"
                        placeholder="What can we help you with?"
                        focusBorderColor={interactivePrimary}
                        borderColor={borderDefault}
                        bg={useColorModeValue('whiteAlpha.900', 'blackAlpha.300')}
                        borderRadius="md"
                        size="lg"
                        _placeholder={{ color: textMuted }}
                        _hover={{ borderColor: borderDefault }}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontWeight="medium" color={textMuted} fontFamily="heading">
                        Message
                      </FormLabel>
                      <Textarea
                        name="message"
                        placeholder="Please provide details about your inquiry, research question, or how we can assist you..."
                        rows={6}
                        focusBorderColor={interactivePrimary}
                        borderColor={borderDefault}
                        bg={useColorModeValue('whiteAlpha.900', 'blackAlpha.300')}
                        borderRadius="md"
                        size="lg"
                        _placeholder={{ color: textMuted }}
                        _hover={{ borderColor: borderDefault }}
                      />
                    </FormControl>

                    <Button
                      type="submit"
                      size="lg"
                      width={{ base: "full", md: "auto" }}
                      px={12}
                      variant="primary"
                      fontFamily="heading"
                      letterSpacing="wide"
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'lg',
                      }}
                      _active={{
                        transform: 'translateY(0)',
                        boxShadow: 'md',
                      }}
                    >
                      Send Message
                    </Button>
                  </VStack>
                </form>
              </Box>
            </VStack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
